import { useEffect } from 'react';
import {
    Title, Box, Stack, Table, Badge, Button, Group, Modal, TextInput, Textarea, Select, Switch,
    Text, Loader, Center, ActionIcon, Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Plus, Trash2, Pencil, Info } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import { SsoPageViewModel, PROVIDER_TEMPLATES } from './SsoPageViewModel';

export function SsoPage(): React.ReactElement {
    const vm = useViewModel(SsoPageViewModel);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    async function handleSave(): Promise<void> {
        const result = await vm.save();
        if (result.success) {
            notifications.show({
                title: result.editing ? 'SSO updated' : 'SSO created',
                message: result.displayName,
                color: 'green',
            });
        } else if (result.error) {
            notifications.show({ title: result.error === 'Alias and Display Name are required' ? 'Missing fields' : 'Error', message: result.error, color: 'red' });
        }
    }

    async function handleDelete(alias: string, displayName: string): Promise<void> {
        if (!confirm(`Delete SSO config "${displayName}"?`)) return;
        const result = await vm.deleteConfig(alias);
        if (result.success) {
            notifications.show({ title: 'SSO deleted', message: displayName, color: 'green' });
        } else if (result.error) {
            notifications.show({ title: 'Error', message: result.error, color: 'red' });
        }
    }

    if (vm.loading) return <Center><Loader /></Center>;

    return (
        <Box>
            <PageHeaderComponent
                title="Single Sign-On"
                subtitle="Configure identity providers for this tenant"
                rightToolbar={<Button leftSection={<Plus size={16} />} onClick={() => vm.openCreate()}>Add SSO Provider</Button>}
            />

            <Alert variant="light" color="blue" icon={<Info size={16} />} mb="md">
                Each tenant can attach multiple SSO providers. Users log in via any configured provider —
                Keycloak handles the OAuth / SAML dance and maps their identity into this tenant's realm.
            </Alert>

            <SurfaceCard $padding="lg" $static>
                {vm.configs && vm.configs.length === 0 && (
                    <Center p="xl">
                        <Stack align="center" gap="xs">
                            <Text c="dimmed">No SSO providers configured</Text>
                            <Text size="xs" c="dimmed">Users log in with Keycloak-local passwords.</Text>
                        </Stack>
                    </Center>
                )}

                {vm.configs && vm.configs.length > 0 && (
                    <Table striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Display Name</Table.Th>
                                <Table.Th>Alias</Table.Th>
                                <Table.Th>Provider</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th w={100}></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {vm.configs.map((c) => (
                                <Table.Tr key={c.alias}>
                                    <Table.Td><Text fw={500}>{c.displayName}</Text></Table.Td>
                                    <Table.Td><Text size="sm" ff="monospace">{c.alias}</Text></Table.Td>
                                    <Table.Td><Badge size="sm" variant="light">{c.providerId}</Badge></Table.Td>
                                    <Table.Td>
                                        <Badge size="sm" color={c.enabled ? 'green' : 'gray'}>
                                            {c.enabled ? 'Enabled' : 'Disabled'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap={4}>
                                            <ActionIcon variant="subtle" size="sm" onClick={() => vm.openEdit(c)}>
                                                <Pencil size={14} />
                                            </ActionIcon>
                                            <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleDelete(c.alias, c.displayName)}>
                                                <Trash2 size={14} />
                                            </ActionIcon>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </SurfaceCard>

            <Modal
                opened={vm.modalOpen}
                onClose={() => vm.closeModal()}
                title={vm.editing ? `Edit SSO: ${vm.form.alias}` : 'Add SSO Provider'}
                size="lg"
            >
                <Stack>
                    <Select
                        label="Provider Type"
                        data={PROVIDER_TEMPLATES.map((p) => ({ value: p.value, label: p.label }))}
                        value={vm.form.providerId}
                        disabled={vm.editing}
                        onChange={(val) => val && vm.setProvider(val)}
                    />
                    <TextInput
                        label="Alias"
                        description="URL-safe identifier (e.g. 'google-acme'). Shown on the login page."
                        placeholder="google-acme"
                        required
                        disabled={vm.editing}
                        value={vm.form.alias}
                        onChange={(e) => vm.setAlias(e.currentTarget.value)}
                    />
                    <TextInput
                        label="Display Name"
                        description="Shown on the Keycloak login page button"
                        placeholder="Acme Google Workspace"
                        required
                        value={vm.form.displayName}
                        onChange={(e) => vm.setDisplayName(e.currentTarget.value)}
                    />

                    <Title order={5} mt="sm">{vm.template.label} Configuration</Title>
                    {vm.template.fields.map((field) =>
                        field.key === 'signingCertificate' ? (
                            <Textarea
                                key={field.key}
                                label={field.label}
                                placeholder="-----BEGIN CERTIFICATE-----..."
                                required={field.required}
                                minRows={4}
                                value={vm.form.config[field.key] ?? ''}
                                onChange={(e) => vm.setConfigField(field.key, e.currentTarget.value)}
                            />
                        ) : (
                            <TextInput
                                key={field.key}
                                label={field.label}
                                placeholder={field.placeholder}
                                type={field.secret ? 'password' : 'text'}
                                required={field.required}
                                value={vm.form.config[field.key] ?? ''}
                                onChange={(e) => vm.setConfigField(field.key, e.currentTarget.value)}
                            />
                        )
                    )}

                    <Switch
                        label="Enabled"
                        description="Show this provider on the login page"
                        checked={vm.form.enabled}
                        onChange={(e) => vm.setEnabled(e.currentTarget.checked)}
                    />

                    <Button onClick={handleSave} loading={vm.saving} fullWidth mt="sm">
                        {vm.editing ? 'Update Provider' : 'Add Provider'}
                    </Button>
                </Stack>
            </Modal>
        </Box>
    );
}
